import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { LeadRecord } from '@flashpoint/site-dna';
import { onLeadSnapshot, patchLead } from '../lib/firestore';
import {
  activeSteps,
  defaultStep,
  indexOfStep,
  nextStep,
  prevStep,
  type WizardStepId,
} from './wizardSteps';
import { useLeadDraft } from '../lib/useLeadDraft';
import { EngagementStep } from './steps/EngagementStep';
import { BusinessStep } from './steps/BusinessStep';
import { DomainStep } from './steps/DomainStep';
import { MigrationStep } from './steps/MigrationStep';
import { ContentStep } from './steps/ContentStep';
import { ContextStep } from './steps/ContextStep';
import { VibeStep } from './steps/VibeStep';
import { LayoutStep } from './steps/LayoutStep';
import { PaletteStep } from './steps/PaletteStep';
import { AssetsStep } from './steps/AssetsStep';
import { SectionsStep } from './steps/SectionsStep';
import { ReviewStep } from './steps/ReviewStep';

export function WizardRoute() {
  const { id } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const [lead, setLead] = useState<LeadRecord | null | undefined>(undefined);

  useEffect(() => {
    if (!id) return;
    return onLeadSnapshot(id, setLead);
  }, [id]);

  /** Longer debounce = fewer Firestore writes + less snapshot churn while typing fast. */
  const draft = useLeadDraft(id ?? '__missing__', { debounceMs: 1100 });

  const steps = useMemo(() => (lead ? activeSteps(lead) : []), [lead]);
  const currentParam = (params.get('step') as WizardStepId | null) ?? null;
  const current: WizardStepId | null = useMemo(() => {
    if (!lead) return null;
    if (currentParam && steps.some((s) => s.id === currentParam)) return currentParam;
    return defaultStep(lead);
  }, [lead, steps, currentParam]);

  // Keep wizardCursor in Firestore in sync with the URL.
  useEffect(() => {
    if (!lead || !current) return;
    if (lead.wizardCursor !== current) {
      void patchLead(lead.id, { wizardCursor: current });
    }
  }, [lead, current]);

  if (lead === undefined) return <div className="font-mono text-sm text-nm-muted">Loading wizard...</div>;
  if (lead === null) {
    return (
      <div className="rounded-xl border border-nm-border bg-nm-moss/30 p-10 text-center">
        <h2 className="font-display text-lg font-semibold text-nm-ink">Lead not found</h2>
        <Link to="/leads" className="mt-3 text-sm text-nm-accent hover:underline">
          Back to leads
        </Link>
      </div>
    );
  }
  if (!current) return null;

  const stepIndex = indexOfStep(steps, current);
  const total = steps.length;
  const stepDescriptor = steps[stepIndex]!;
  const previous = prevStep(steps, current);
  const next = nextStep(steps, current);

  const goto = (id: WizardStepId | null) => {
    if (!id) return;
    setParams({ step: id }, { replace: false });
  };

  const onBack = async () => {
    await draft.flush();
    if (previous) goto(previous);
    else navigate(`/leads/${lead.id}`);
  };

  const onNext = async () => {
    await draft.flush();
    if (next) goto(next);
    else navigate(`/leads/${lead.id}/provisioning`);
  };

  const onSaveExit = async () => {
    await draft.flush();
    navigate(`/leads/${lead.id}/wizard?step=${current}`);
    navigate(`/leads/${lead.id}`);
  };

  const stepProps = { lead, queueField: draft.queueField, flush: draft.flush };

  return (
    <div className="space-y-4">
      <div>
        <Link
          to={`/leads/${lead.id}`}
          className="font-mono text-[10px] uppercase tracking-[0.2em] text-nm-muted hover:text-nm-accent"
        >
          &larr; Back to {lead.siteDna.businessInfo.name?.trim() || 'lead'}
        </Link>
      </div>

      <div className="rounded-xl border border-nm-border bg-nm-moss/25">
        <div className="border-b border-nm-border p-4">
          <div className="flex items-end justify-between gap-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-nm-muted">
                Sheet {stepIndex + 1} of {total}
              </div>
              <h2 className="font-display text-xl font-semibold text-nm-ink">{stepDescriptor.title}</h2>
              <p className="mt-0.5 font-mono text-[11px] text-nm-muted">{stepDescriptor.subtitle}</p>
            </div>
            <div className="text-right font-mono text-[10px] uppercase tracking-widest text-nm-muted">
              {draft.saving ? (
                <span className="text-nm-accent">Saving…</span>
              ) : draft.error ? (
                <span className="text-nm-danger">Save failed — retrying</span>
              ) : draft.lastSavedAt ? (
                <span className="text-nm-ink">Saved</span>
              ) : (
                <span>Draft</span>
              )}
            </div>
          </div>
          <div className="mt-3 h-1 overflow-hidden rounded bg-nm-border">
            <div
              className="h-full bg-nm-accent transition-[width]"
              style={{ width: `${((stepIndex + 1) / total) * 100}%` }}
            />
          </div>
        </div>

        <div className="p-4">
          {current === 'engagement' && <EngagementStep {...stepProps} />}
          {current === 'business' && <BusinessStep {...stepProps} />}
          {current === 'domain' && <DomainStep {...stepProps} />}
          {current === 'migration' && <MigrationStep {...stepProps} />}
          {current === 'content' && <ContentStep {...stepProps} />}
          {current === 'context' && <ContextStep {...stepProps} />}
          {current === 'vibe' && <VibeStep {...stepProps} />}
          {current === 'layout' && <LayoutStep {...stepProps} />}
          {current === 'palette' && <PaletteStep {...stepProps} />}
          {current === 'assets' && <AssetsStep {...stepProps} />}
          {current === 'sections' && <SectionsStep {...stepProps} />}
          {current === 'review' && <ReviewStep {...stepProps} />}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-nm-border p-4">
          <button
            type="button"
            onClick={() => void onBack()}
            className="rounded-full border border-nm-border px-4 py-2 font-mono text-[11px] uppercase tracking-wide text-nm-muted hover:border-nm-accent hover:text-nm-ink"
          >
            Back
          </button>
          <button
            type="button"
            onClick={() => void onSaveExit()}
            className="rounded-full font-mono text-[10px] uppercase tracking-widest text-nm-muted hover:text-nm-accent"
          >
            Save &amp; exit
          </button>
          <button
            type="button"
            onClick={() => void onNext()}
            className="rounded-full bg-nm-accent px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-wide text-nm-on-accent hover:bg-nm-accent-hover"
          >
            {next ? 'Next' : 'Done'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Re-export for router config that may want to redirect /wizard -> default step.
export function WizardIndexRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/leads/${id}/wizard?step=engagement`} replace />;
}
